import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTillId } from '../utils/tillId';
import { useUsageTracker } from '../hooks/useUsageTracker';
import { useStore } from '../context/StoreContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  History, 
  LogOut, 
  Menu, 
  X,
  User,
  Calculator,
  Landmark,
  Upload,
  ClipboardList,
  Clock,
  BarChart3,
  Users,
  UserCog,
  FileText,
  Bell,
  ShoppingBag,
  Truck,
  ArrowRightLeft,
  Settings,
  Store,
  ChevronDown,
  ChevronRight,
  Scan,
  Printer,
  Banknote,
  ClipboardCheck,
  CheckSquare,
  Zap,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  MessageCircle,
  Megaphone,
  BookOpen,
  Mail,
  CalendarDays,
  Sparkles,
  HeartPulse,
  ShieldCheck,
  Building2,
  Eye,
  UsersRound,
  Contact
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { TillNudgeBanner } from './TillNudgeBanner';
import { TrialTour } from './TrialTour';

// Trial banner: live countdown inside the last two hours, otherwise the end date/time.
const TrialBannerText = ({ endsAt }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(id); }, []);
  if (!endsAt) return <span>Demo — your 1-hour trial starts when you first sign in. Sample stock and sales are included so you can explore.</span>;
  const mins = Math.round((new Date(endsAt).getTime() - now) / 60000);
  if (mins <= 0) return <span>Demo — this trial has ended. Email us for more time.</span>;
  if (mins <= 120) return <span data-testid="trial-countdown">Demo — <b>{mins} minute{mins === 1 ? '' : 's'} left</b> in your SupplyTrack trial. Want more time? Just reply to your trial email.</span>;
  return <span>Demo — this SupplyTrack trial ends {new Date(endsAt).toLocaleString('en-NZ', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}. Sample stock and sales are included so you can explore.</span>;
};

const Layout = ({ children }) => {
  const { user, logout, activeStaff, staffLogout, setShowStaffLogin, token, hasPermission } = useAuth();
  const { stores, selectedStore, changeStore, getCurrentStore, storeName, isMultiStore } = useStore();
  const businessName = user?.business_name || 'SupplyTrack';
  const location = useLocation();
  const navigate = useNavigate();
  useUsageTracker();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ inventory: true });
  const [customerOrderCount, setCustomerOrderCount] = useState(0);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [pendingShiftAlerts, setPendingShiftAlerts] = useState(0);
  const [unreadNoticeCount, setUnreadNoticeCount] = useState(0);
  const [customerRequestCount, setCustomerRequestCount] = useState(0);
  const prevNoticeCountRef = React.useRef(0);
  const prevCustomerOrderCountRef = React.useRef(-1);
  const prevCustomerRequestCountRef = React.useRef(-1);
  const dingAudioRef = React.useRef(null);

  // Detect current store from context (read from localStorage as fallback)
  const currentStore = (typeof window !== 'undefined' && localStorage.getItem('selectedStore')) || 'orewa';

  // Play "ding" sound using Web Audio (no asset needed)
  const playDing = React.useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine';
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.18, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      o.start();
      o.stop(ctx.currentTime + 0.5);
      // second softer ding for "two-tone"
      setTimeout(() => {
        try {
          const o2 = ctx.createOscillator();
          const g2 = ctx.createGain();
          o2.connect(g2); g2.connect(ctx.destination);
          o2.type = 'sine';
          o2.frequency.value = 660;
          g2.gain.setValueAtTime(0.15, ctx.currentTime);
          g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
          o2.start();
          o2.stop(ctx.currentTime + 0.5);
        } catch { /* ignore */ }
      }, 180);
    } catch { /* browser blocks audio before user interacts */ }
  }, []);

  // Poll unread notice count every 25 sec across the whole app
  useEffect(() => {
    if (!token) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
    const refresh = () => {
      fetch(`${API_URL}/notice-board/unread-count?store=${encodeURIComponent(currentStore)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(d => {
          const newCount = d.count || 0;
          // Only ding when count INCREASED (not on page load)
          if (newCount > prevNoticeCountRef.current && prevNoticeCountRef.current >= 0) {
            playDing();
          }
          prevNoticeCountRef.current = newCount;
          setUnreadNoticeCount(newCount);
        })
        .catch(() => {});
    };
    // Initial fetch: don't ding (set ref first)
    prevNoticeCountRef.current = -1;
    refresh();
    const interval = setInterval(refresh, 25000);
    return () => clearInterval(interval);
  }, [token, currentStore, playDing]);

  // Mark notices as seen when user opens Notice Board page (resets the badge)
  useEffect(() => {
    if (location.pathname === '/notice-board' && token) {
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      fetch(`${API_URL}/notice-board/mark-seen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ store: currentStore }),
      }).then(() => setUnreadNoticeCount(0)).catch(() => {});
    }
  }, [location.pathname, token, currentStore]);

  // Poll pending shift alerts so the red bar shows on every page
  useEffect(() => {
    if (!token) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
    const refresh = () => {
      fetch(`${API_URL}/shift-alerts/pending-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => setPendingShiftAlerts(d.count || 0))
        .catch(() => {});
    };
    refresh();
    const interval = setInterval(refresh, 60000); // 1 min poll
    const onChange = () => refresh();
    window.addEventListener('shift-alerts-changed', onChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('shift-alerts-changed', onChange);
    };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

    const refreshCounts = () => {
      // Pending customer ORDERS (special orders awaiting fulfilment)
      fetch(`${API_URL}/customer-orders/count`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          const newCount = d.count || 0;
          if (prevCustomerOrderCountRef.current >= 0 && newCount > prevCustomerOrderCountRef.current) {
            playDing();
            try { toast.info(`📦 New customer order — ${newCount - prevCustomerOrderCountRef.current} just added`); } catch (_) {}
          }
          prevCustomerOrderCountRef.current = newCount;
          setCustomerOrderCount(newCount);
        })
        .catch(() => {});

      // Pending customer REQUESTS (product wants, not stocked)
      fetch(`${API_URL}/customer-requests/count?store=${encodeURIComponent(currentStore)}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          const newCount = d.count || 0;
          if (prevCustomerRequestCountRef.current >= 0 && newCount > prevCustomerRequestCountRef.current) {
            playDing();
            try { toast.info(`🔔 New customer product request`); } catch (_) {}
          }
          prevCustomerRequestCountRef.current = newCount;
          setCustomerRequestCount(newCount);
        })
        .catch(() => {});

      // Unread chat messages — "mine" = sent from this till (all tills share one login, so sender_id is useless).
      // Lightweight summary only: never pull the photo payloads just to draw a badge.
      const lastSeen = localStorage.getItem('chat_last_seen') || '2000-01-01';
      const myTill = getTillId();
      const myName = (activeStaff?.name || user?.name || '').trim();
      fetch(`${API_URL}/chat/summary?since=${encodeURIComponent(lastSeen)}&till_id=${encodeURIComponent(myTill)}&name=${encodeURIComponent(myName)}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(summary => {
          const newestOther = summary.newest_other;
          const lastAlerted = localStorage.getItem('chat_last_alerted') || '2000-01-01';
          if (newestOther && newestOther.created_at > lastAlerted && newestOther.created_at > lastSeen && location.pathname !== '/store-chat') {
            localStorage.setItem('chat_last_alerted', newestOther.created_at);
            playDing();
            const snippet = newestOther.text ? newestOther.text.slice(0, 80) : '📷 Photo';
            try {
              toast.message(`💬 ${newestOther.sender_name} (${storeName(newestOther.store)})`, {
                description: snippet, duration: 8000,
                action: { label: 'Open chat', onClick: () => navigate('/store-chat') },
              });
            } catch (_) {}
            try {
              if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
                new Notification(`Store chat — ${newestOther.sender_name}`, { body: snippet, tag: 'store-chat' });
              }
            } catch (_) {}
          }
          setUnreadChatCount(summary.unread || 0);
        })
        .catch(() => {});
    };

    refreshCounts();
    const interval = setInterval(refreshCounts, 20000);
    return () => clearInterval(interval);
  }, [token, location.pathname, currentStore, user?.id, user?.name, activeStaff?.name, playDing, navigate]);

  // Ask once for browser notification permission so chat alerts reach a till that's on another tab
  useEffect(() => {
    try {
      if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission().catch(() => {});
    } catch (_) {}
  }, []);

  // Mark chat as read when visiting chat page
  useEffect(() => {
    if (location.pathname === '/store-chat') {
      localStorage.setItem('chat_last_seen', new Date().toISOString());
      setUnreadChatCount(0);
    }
  }, [location.pathname]);

  // Main nav items (not nested)
  const mainNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/pos', label: 'POS', icon: Calculator },
    { path: '/till', label: 'Till', icon: Banknote },
    // Shopify is connected to the platform owner's business only
    ...(user?.is_platform_business ? [{ path: '/ecommerce', label: 'Ecommerce', icon: ShoppingBag }] : []),
  ];

  // Inventory sub-items
  const inventorySubItems = [
    { path: '/inventory', label: 'Products', icon: Package },
    { path: '/stock-count', label: 'Stock Count', icon: ClipboardCheck },
    { path: '/smart-po', label: 'Smart PO', icon: ClipboardList },
    { path: '/stock-transfer', label: 'Transfer', icon: ArrowRightLeft },
    ...(user?.is_platform_business ? [{ path: '/transfer-history', label: 'Royal Oak Transfers', icon: ArrowRightLeft }] : []),
    { path: '/suppliers', label: 'Suppliers', icon: Truck },
    { path: '/quick-keys', label: 'Quick Keys', icon: Zap },
    { path: '/short-dated', label: 'Short Dated', icon: AlertTriangle },
    { path: '/inventory-health', label: 'Inventory Health', icon: ShieldCheck },
    ...(user?.protocol_addon !== false ? [{ path: '/protocols', label: 'Protocols', icon: HeartPulse }] : []),
  ];

  // Customers sub-items
  const customerItems = [
    { path: '/customers', label: 'Customers', icon: Users },
    { path: '/customer-orders', label: 'Customer Orders', icon: ClipboardList, hasBadge: true },
    { path: '/ordered-waiting', label: 'Ordered & Waiting', icon: Clock },
    { path: '/customer-requests', label: 'Customer Requests', icon: Package, hasRequestBadge: true },
    { path: '/reminders', label: 'Reminders', icon: Bell },
  ];

  // Admin sub-items
  const adminItems = [
    ...(user?.is_platform_admin ? [{ path: '/admin', label: 'Admin Console', icon: Building2 }] : []),
    { path: '/marketing', label: 'Marketing', icon: Mail },
    { path: '/invoices', label: 'Invoices', icon: FileText },
    ...(user?.is_platform_business ? [{ path: '/shopify-health', label: 'Shopify Health', icon: Sparkles }] : []),
    ...(hasPermission('admin') ? [{ path: '/gst-return', label: 'GST Return', icon: Landmark }] : []),
    { path: '/store-settings', label: 'Stores', icon: Store },
    { path: '/hardware', label: 'Hardware', icon: Printer },
  ];

  // Other nav items (top-level)
  const otherNavItems = [
    { path: '/oos-report', label: 'Negative Stock', icon: AlertTriangle },
    { path: '/history', label: 'History', icon: History },
  ];

  // Staff Room sub-items (grouped under collapsible folder)
  const isOwner = (activeStaff?.name || '').trim().toLowerCase() === 'ritu';
  const staffRoomItems = [
    { path: '/notice-board', label: 'Notice Board', icon: Megaphone, hasNoticeBadge: true },
    // Today's Tasks is for staff only — hide it when the owner (Ritu) is signed in
    ...(isOwner ? [] : [{ path: '/staff-tasks', label: 'Today\'s Tasks', icon: CheckSquare }]),
    { path: '/weekly-roster', label: 'Weekly Roster', icon: CalendarDays },
    { path: '/reports', label: 'Sales Reports', icon: BarChart3 },
    { path: '/sales-report', label: 'Supplier / Brand Sales', icon: TrendingUp },
    { path: '/task-accountability', label: 'Task Accountability', icon: ClipboardCheck },
    { path: '/staff-activity', label: 'Staff Activity', icon: ClipboardCheck },
    { path: '/staff', label: 'Staff', icon: UserCog },
    { path: '/store-chat', label: 'Store Chat', icon: MessageCircle, hasChatBadge: true },
    { path: '/help', label: 'Staff Manual', icon: BookOpen },
  ];

  const navGroups = [
    { key: 'staff', label: 'Staff Room', icon: UsersRound, items: staffRoomItems, badge: (unreadNoticeCount || 0) + (unreadChatCount || 0) },
    { key: 'inventory', label: 'Inventory', icon: Package, items: inventorySubItems, badge: 0 },
    { key: 'customers', label: 'Customers', icon: Contact, items: customerItems, badge: (customerOrderCount || 0) + (customerRequestCount || 0) },
    { key: 'admin', label: 'Admin', icon: Settings, items: adminItems, badge: 0 },
  ];

  const activeGroupKey = navGroups.find(g => g.items.some(item => location.pathname === item.path))?.key;
  // Auto-expand the group that holds the current page
  React.useEffect(() => {
    if (activeGroupKey) setExpandedGroups(prev => (prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true }));
  }, [activeGroupKey]);
  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const Badges = ({ item }) => (
    <>
      {item.hasBadge && customerOrderCount > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">{customerOrderCount}</span>
      )}
      {item.hasChatBadge && unreadChatCount > 0 && (
        <span className="bg-blue-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">{unreadChatCount}</span>
      )}
      {item.hasNoticeBadge && unreadNoticeCount > 0 && (
        <span className="bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center animate-pulse">{unreadNoticeCount}</span>
      )}
      {item.hasRequestBadge && customerRequestCount > 0 && (
        <span className="bg-pink-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">{customerRequestCount}</span>
      )}
    </>
  );

  const NavLink = ({ item, isSubItem = false }) => (
    <Link
      to={item.path}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s|'|\//g, '-').replace(/-+/g, '-')}`}
      className={`flex items-center gap-3 px-3 rounded-lg transition-all text-sm ${
        isSubItem ? 'ml-4 py-2 ' : 'py-2.5 font-medium '
      }${
        isActive(item.path)
          ? 'bg-gray-100 text-[#1B5E20] border-l-[3px] border-[#2D3E2F]'
          : isSubItem ? 'text-gray-500 hover:bg-gray-50 hover:text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <item.icon className={isSubItem ? 'w-4 h-4' : 'w-5 h-5'} strokeWidth={1.5} />
      <span className="flex-1">{item.label}</span>
      <Badges item={item} />
    </Link>
  );

  const NavGroup = ({ group }) => {
    const expanded = !!expandedGroups[group.key];
    const active = group.key === activeGroupKey;
    return (
      <div className="pt-2">
        <button
          onClick={() => toggleGroup(group.key)}
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all text-sm font-medium ${
            active ? 'bg-gray-100 text-[#1B5E20]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
          data-testid={`nav-${group.key}-toggle`}
          aria-expanded={expanded}
        >
          <span className="flex items-center gap-3">
            <group.icon className="w-5 h-5" strokeWidth={1.5} />
            <span>{group.label}</span>
            {group.badge > 0 && !expanded && (
              <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid={`nav-${group.key}-badge`}>{group.badge}</span>
            )}
          </span>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {expanded && (
          <div className="mt-1 space-y-0.5" data-testid={`nav-${group.key}-items`}>
            {group.items.map((item) => <NavLink key={item.path} item={item} isSubItem />)}
          </div>
        )}
      </div>
    );
  };

  const MobileLink = ({ item, indent = false }) => (
    <Link
      to={item.path}
      onClick={() => setMobileMenuOpen(false)}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${indent ? 'ml-2 ' : ''}${
        isActive(item.path) ? 'bg-[#C8E6C9] text-[#1B5E20] border-l-[3px] border-[#2D3E2F]' : 'text-[#2D3E2F] hover:bg-[#C8E6C9]'
      }`}
    >
      <item.icon className="w-5 h-5" />
      <span className="font-medium flex-1">{item.label}</span>
      <Badges item={item} />
    </Link>
  );

  return (
    <div className={`min-h-screen bg-[#FAFDF7] ${(user?.impersonated_by || user?.business_status === 'trial') ? 'pt-7' : ''}`}>
      {!user?.impersonated_by && user?.business_status === 'trial' && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-sky-700 text-white text-xs font-semibold px-4 py-1.5 flex items-center justify-center gap-2" data-testid="trial-banner">
          <Sparkles className="w-3.5 h-3.5" /> <TrialBannerText endsAt={user?.trial_ends_at} />
        </div>
      )}
      <TrialTour />
      {user?.impersonated_by && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-amber-950 text-xs font-semibold px-4 py-1.5 flex items-center justify-center gap-2" data-testid="impersonation-banner">
          <Eye className="w-3.5 h-3.5" /> Support view — you are signed in as {businessName}. Changes here affect their live data.
          <button className="underline ml-2" onClick={() => {
            const back = sessionStorage.getItem('adminReturnToken');
            sessionStorage.removeItem('adminReturnToken');
            localStorage.removeItem('staffToken'); localStorage.removeItem('activeStaff');
            if (back) { localStorage.setItem('token', back); window.location.href = '/admin'; } else { logout(); }
          }} data-testid="impersonation-exit">Exit</button>
        </div>
      )}
      {/* Desktop Sidebar - Clean white/neutral */}
      <aside className={`hidden lg:flex flex-col fixed left-0 w-64 bg-white text-[#2D3E2F] z-50 border-r border-gray-200 ${(user?.impersonated_by || user?.business_status === 'trial') ? 'top-7 h-[calc(100vh-1.75rem)]' : 'top-0 h-screen'}`}>
        {/* Logo - Clickable to go to Dashboard */}
        <Link to="/dashboard" className="block px-5 py-5 border-b border-gray-100 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#2D3E2F] rounded-lg flex items-center justify-center shadow-md">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-lg tracking-tight text-[#1B5E20] truncate max-w-[170px]" style={{fontFamily: 'Work Sans, sans-serif'}} data-testid="business-name" title={businessName}>
                {businessName}
              </h1>
              <p className="text-xs text-gray-500">SupplyTrack POS & Inventory</p>
            </div>
          </div>
        </Link>

        {/* Navigation - Clean neutral style with collapsible groups */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
          {mainNavItems.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}

          {navGroups.map((group) => (
            <NavGroup key={group.key} group={group} />
          ))}

          <div className="pt-2 space-y-0.5">
            {otherNavItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </div>
        </nav>

        {/* Store Selector */}
        <div className="p-4 border-t border-gray-100">
          <p className="text-[11px] text-gray-400 mb-2 px-1 uppercase tracking-wider font-medium">Active Store</p>
          {!isMultiStore ? (
            <div className="w-full flex items-center gap-2 bg-gray-50 border border-gray-200 text-gray-700 rounded-lg h-10 px-3 text-sm" data-testid="store-selector">
              <Store className="w-4 h-4 text-amber-500" />
              {getCurrentStore()?.name || 'Store'}
            </div>
          ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full justify-between bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 rounded-lg h-10"
                data-testid="store-selector"
              >
                <span className="flex items-center gap-2">
                  <Store className={`w-4 h-4 ${selectedStore === 'orewa' ? 'text-amber-500' : 'text-[#2D3E2F]'}`} />
                  {getCurrentStore()?.name || 'Select Store'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              {stores.map(store => (
                <DropdownMenuItem 
                  key={store.code}
                  onClick={() => changeStore(store.code)}
                  className={selectedStore === store.code ? 'bg-gray-100' : ''}
                >
                  <Store className={`w-4 h-4 mr-2 ${store.code === 'orewa' ? 'text-amber-500' : 'text-[#2D3E2F]'}`} />
                  {store.name}
                  {selectedStore === store.code && <span className="ml-auto text-[#2D3E2F]">✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>

        {/* User Section */}
        <div className="p-4 border-t border-gray-100">
          {/* Active Staff Info */}
          {activeStaff && (
            <div className="mb-3 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-white">{activeStaff.initials}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-emerald-900">{activeStaff.name}</p>
                    <p className="text-xs text-emerald-600 capitalize">{activeStaff.role}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={staffLogout}
                  className="text-emerald-700 hover:bg-emerald-100 h-8 px-2"
                  title="Switch User"
                  data-testid="switch-staff-button"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Store Owner Info */}
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.login_email || user?.email}</p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-2 mt-2">
            {!activeStaff && (
              <Button 
                onClick={() => setShowStaffLogin(true)}
                variant="outline"
                className="flex-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50 justify-center rounded-lg text-sm"
                data-testid="staff-login-button"
              >
                <Users className="w-4 h-4 mr-1" /> Staff Login
              </Button>
            )}
            <Button 
              onClick={handleLogout}
              variant="ghost"
              className="flex-1 text-slate-600 hover:text-red-600 hover:bg-red-50 justify-center rounded-lg text-sm"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-1" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header - Pale green style */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#E8F5E9] text-[#2D3E2F] z-50 flex items-center justify-between px-4 border-b border-[#C8E6C9]">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#2D3E2F] rounded-lg flex items-center justify-center">
            <Package className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-[#1B5E20] truncate max-w-[180px]" style={{fontFamily: 'Work Sans, sans-serif'}} data-testid="business-name-mobile">{businessName}</span>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-[#2D3E2F]">
                <User className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button 
            variant="ghost" 
            size="icon"
            className="text-[#2D3E2F]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileMenuOpen(false)}>
          <nav className="absolute top-14 left-0 right-0 bg-[#E8F5E9] p-3 space-y-0.5 border-b border-[#C8E6C9] max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {mainNavItems.map((item) => <MobileLink key={item.path} item={item} />)}

            {navGroups.map((group) => (
              <div key={group.key} className="pt-2 border-t border-[#C8E6C9] mt-2">
                <div className="px-3 py-2 text-xs font-semibold text-[#1B5E20] uppercase">{group.label}</div>
                {group.items.map((item) => <MobileLink key={item.path} item={item} indent />)}
              </div>
            ))}

            <div className="pt-2 border-t border-[#C8E6C9] mt-2">
              {otherNavItems.map((item) => <MobileLink key={item.path} item={item} />)}
            </div>
          </nav>
        </div>
      )}

      {/* Main Content - Soft wellness background */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen bg-[#FAFDF7]">
        {/* Global Shift Coverage banner — solid red, sticky, until all alerts resolved */}
        {pendingShiftAlerts > 0 && (
          <Link
            to="/notice-board"
            className="block sticky top-14 lg:top-0 z-30 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 shadow-md cursor-pointer transition-colors"
            data-testid="global-shift-alert-banner"
          >
            <div className="flex items-center justify-between gap-3 max-w-screen-2xl">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <AlertTriangle className="w-4 h-4 shrink-0 animate-pulse" />
                <span>
                  {pendingShiftAlerts === 1 ? '1 staff shift needs cover' : `${pendingShiftAlerts} staff shifts need cover`}
                  {' '}— click to assign
                </span>
              </div>
              <span className="text-xs underline">Open Notice Board →</span>
            </div>
          </Link>
        )}
        {/* Daily till routine — open / close-yesterday / close-now reminders */}
        <TillNudgeBanner />
        <div className="p-4 lg:p-6 xl:p-8 w-full">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
